import Phaser from 'phaser';
import type { GameState, LogEntry, ReplayStep, Side } from '~shared/types';
import { HexBoard } from '../objects/HexBoard';
import { UnitSprite } from '../objects/Unit';
import { socketClient } from '../net/SocketClient';

const W = 1440, H = 768;
const BOARD_X = 284;
const BOARD_Y = 95;
const PANEL_L_CX = 120;
const BOARD_MID_X = BOARD_X + 430;
const BTN_Y = 700;

const LOG_COLOR: Record<LogEntry['type'], string> = {
  'card':        '#d4af37',
  'order':       '#88aaff',
  'move':        '#88dd88',
  'attack':      '#ff8866',
  'eliminate':   '#ff4444',
  'retreat':     '#ffdd44',
  'wire-remove': '#44ddcc',
  'medal':       '#ffd700',
  'airdrop':     '#88eeff',
  'turn-end':    '#556655',
};

/**
 * Read-only move-by-move / turn-by-turn viewer for a finished game. Reuses the
 * scene-agnostic HexBoard/UnitSprite and the existing review-overlay mechanism
 * (HexBoard.showReviewOverlay), driven by a server-built "move index" — one
 * entry per snapshot where the action log grew — with GameStates lazily loaded
 * and cached per step. See plan: reflective-dancing-muffin.
 */
export class ReplayScene extends Phaser.Scene {
  private roomId!: string;

  private moveIndex: ReplayStep[] = [];
  private initialSeq = 0;
  private finalSeq   = 0;
  private scenarioName = '';

  // currentStepIdx === -1 means "initial state, before the first logged move".
  private currentStepIdx = -1;
  private pendingTargetIdx: number | null = null;
  private stateCache = new Map<number, GameState>();
  private pendingSeqRequests = new Set<number>();

  private board!: HexBoard;
  private unitSprites = new Map<string, UnitSprite>();

  private headerText!: Phaser.GameObjects.Text;
  private turnText!:   Phaser.GameObjects.Text;
  private medalText!:  Phaser.GameObjects.Text;
  private stepText!:   Phaser.GameObjects.Text;
  private logDiv!:     HTMLDivElement;

  private prevTurnBtn!: Phaser.GameObjects.Rectangle;
  private prevMoveBtn!: Phaser.GameObjects.Rectangle;
  private nextMoveBtn!: Phaser.GameObjects.Rectangle;
  private nextTurnBtn!: Phaser.GameObjects.Rectangle;
  private navButtons:   Phaser.GameObjects.Rectangle[] = [];

  private loadingText!: Phaser.GameObjects.Text;
  private gameOverObjects: Phaser.GameObjects.GameObject[] = [];

  constructor() { super({ key: 'ReplayScene' }); }

  init(data: { roomId: string }): void {
    this.roomId = data.roomId;
    this.moveIndex = [];
    this.currentStepIdx = -1;
    this.pendingTargetIdx = null;
    this.stateCache = new Map();
    this.pendingSeqRequests = new Set();
    this.gameOverObjects = [];
    this.cameras?.main?.setZoom(1).setScroll(0, 0);
  }

  create(): void {
    this.events.once('shutdown', this.shutdown, this);

    this.add.rectangle(W / 2, H / 2, W, H, 0x1a2d1a);

    // ── Header bar ───────────────────────────────────────────────────
    this.add.rectangle(W / 2, 24, W, 48, 0x0d1a0d).setDepth(1);

    const lobbyBtn = this.add.rectangle(60, 24, 90, 30, 0x243824)
      .setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => lobbyBtn.setFillStyle(0x3a5a3a))
      .on('pointerout',  () => lobbyBtn.setFillStyle(0x243824))
      .on('pointerdown', () => this.backToLobby());
    this.add.text(60, 24, '← Lobby', { fontSize: '11px', color: '#aabbaa', fontStyle: 'bold' })
      .setOrigin(0.5).setDepth(3);

    this.headerText = this.add.text(170, 8, 'MEMOIR \'44 · REPLAY', {
      fontSize: '13px', color: '#d4af37', fontStyle: 'bold',
    }).setDepth(2);
    this.turnText  = this.add.text(W / 2, 8,  '', { fontSize: '13px', color: '#ffffff' }).setOrigin(0.5, 0).setDepth(2);
    this.medalText = this.add.text(W - 10, 8, '', { fontSize: '13px', color: '#aaffaa' }).setOrigin(1, 0).setDepth(2);
    this.stepText  = this.add.text(W / 2, 28, '', { fontSize: '11px', color: '#cccccc' }).setOrigin(0.5, 0).setDepth(2);

    // ── Left panel: event log ────────────────────────────────────────
    this.add.rectangle(242, 384, 2, 768, 0x334433);
    this.add.text(8, 57, 'GAME LOG', { fontSize: '9px', color: '#777777', fontStyle: 'bold' });
    this.logDiv = document.createElement('div');
    this.logDiv.style.cssText = [
      'width:234px', 'height:600px', 'overflow-y:auto', 'box-sizing:border-box',
      'font-family:monospace', 'font-size:11px', 'line-height:1.5',
      'color:#99bb99', 'background:#080f08', 'border:1px solid #334433',
      'border-radius:3px', 'padding:4px 6px',
    ].join(';');
    this.add.dom(PANEL_L_CX, 380, this.logDiv);

    // ── Board ────────────────────────────────────────────────────────
    this.board = new HexBoard(this, BOARD_X, BOARD_Y);

    this.loadingText = this.add.text(W / 2, H / 2, 'Loading replay…', {
      fontSize: '18px', color: '#cccccc',
    }).setOrigin(0.5).setDepth(10);

    // ── Bottom control bar ───────────────────────────────────────────
    this.buildControlBar();

    // ── Socket wiring ────────────────────────────────────────────────
    socketClient.socket.on('replay_index', (payload) => this.onReplayIndex(payload));
    socketClient.socket.on('replay_state', ({ seq, state }) => this.onReplayState(seq, state));
    socketClient.socket.on('replay_error', (msg) => {
      this.loadingText.setText(`Could not load replay: ${msg}`).setVisible(true);
    });
    socketClient.socket.emit('load_replay', { roomId: this.roomId });
  }

  shutdown(): void {
    const s = socketClient.socket;
    s.off('replay_index');
    s.off('replay_state');
    s.off('replay_error');
  }

  // ── Setup ──────────────────────────────────────────────────────────

  private buildControlBar(): void {
    const mk = (x: number, label: string, onClick: () => void): Phaser.GameObjects.Rectangle => {
      const btn = this.add.rectangle(x, BTN_Y, 110, 40, 0x336633)
        .setInteractive({ useHandCursor: true })
        .on('pointerover', () => { if (btn.alpha > 0.4) btn.setFillStyle(0x44aa44); })
        .on('pointerout',  () => { if (btn.alpha > 0.4) btn.setFillStyle(0x336633); })
        .on('pointerdown', () => { if (btn.alpha > 0.4) onClick(); });
      this.add.text(x, BTN_Y, label, { fontSize: '12px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0.5);
      this.navButtons.push(btn);
      return btn;
    };

    this.prevTurnBtn = mk(BOARD_MID_X - 250, '⏮ Prev Turn', () => this.goToIdx(this.findPrevTurnIdx()));
    this.prevMoveBtn = mk(BOARD_MID_X - 125, '◀ Prev Move', () => this.goToIdx(this.currentStepIdx - 1));
    this.nextMoveBtn = mk(BOARD_MID_X + 125, 'Next Move ▶', () => this.goToIdx(this.currentStepIdx + 1));
    this.nextTurnBtn = mk(BOARD_MID_X + 250, 'Next Turn ⏭', () => this.goToIdx(this.findNextTurnIdx()));
  }

  private backToLobby(): void {
    localStorage.removeItem('memoir44_rejoin');
    window.location.reload();
  }

  // ── Index / state loading ──────────────────────────────────────────

  private onReplayIndex(payload: {
    roomId: string; scenarioId: string; scenarioName: string;
    initialSeq: number; finalSeq: number; steps: ReplayStep[]; initialState: GameState;
  }): void {
    this.moveIndex    = payload.steps;
    this.initialSeq   = payload.initialSeq;
    this.finalSeq     = payload.finalSeq;
    this.scenarioName = payload.scenarioName;
    this.headerText.setText(`MEMOIR '44 · REPLAY · ${payload.scenarioName.toUpperCase()}`);

    this.stateCache.set(payload.initialSeq, payload.initialState);
    this.loadingText.setVisible(false);
    this.currentStepIdx = -1;
    this.renderStep(payload.initialState);
  }

  private onReplayState(seq: number, state: GameState): void {
    this.stateCache.set(seq, state);
    this.pendingSeqRequests.delete(seq);
    if (this.pendingTargetIdx !== null && this.seqForIdx(this.pendingTargetIdx) === seq) {
      this.currentStepIdx  = this.pendingTargetIdx;
      this.pendingTargetIdx = null;
      this.renderStep(state);
    }
  }

  private seqForIdx(idx: number): number {
    return idx === -1 ? this.initialSeq : this.moveIndex[idx].seq;
  }

  private requestState(seq: number): void {
    if (this.stateCache.has(seq) || this.pendingSeqRequests.has(seq)) return;
    this.pendingSeqRequests.add(seq);
    socketClient.socket.emit('load_replay_state', { roomId: this.roomId, seq });
  }

  private goToIdx(idx: number): void {
    const clamped = Math.max(-1, Math.min(idx, this.moveIndex.length - 1));
    if (clamped === this.currentStepIdx) return;
    const seq = this.seqForIdx(clamped);
    const cached = this.stateCache.get(seq);
    if (cached) {
      this.pendingTargetIdx = null;
      this.currentStepIdx = clamped;
      this.renderStep(cached);
    } else {
      this.pendingTargetIdx = clamped;
      this.requestState(seq);
    }
  }

  private findPrevTurnIdx(): number {
    for (let i = this.currentStepIdx - 1; i >= 0; i--) {
      if (this.moveIndex[i].isTurnEnd) return i;
    }
    return -1;
  }

  private findNextTurnIdx(): number {
    for (let i = this.currentStepIdx + 1; i < this.moveIndex.length; i++) {
      if (this.moveIndex[i].isTurnEnd) return i;
    }
    return this.moveIndex.length - 1;
  }

  private prefetchNeighbors(): void {
    for (const idx of [this.currentStepIdx - 1, this.currentStepIdx + 1]) {
      if (idx < -1 || idx > this.moveIndex.length - 1) continue;
      this.requestState(this.seqForIdx(idx));
    }
  }

  // ── Rendering ──────────────────────────────────────────────────────

  private renderStep(state: GameState): void {
    this.board.render(state.terrain, state.specialHexes, false, state.terrainTheme);
    this.renderUnits(state);

    this.board.clearReviewOverlay();
    if (this.currentStepIdx >= 0) {
      this.board.showReviewOverlay(this.moveIndex[this.currentStepIdx].newEntries);
    }

    this.turnText.setText(`Turn ${state.turn}  ·  ${state.currentSide.toUpperCase()}`);
    this.medalText.setText(
      `Allies ${state.alliesMedals}/${state.alliesMedalsToWin}   Axis ${state.axisMedals}/${state.axisMedalsToWin}`,
    );

    const total = this.moveIndex.length;
    if (this.currentStepIdx === -1) {
      this.stepText.setText(`Initial position  ·  Move 0 / ${total}`);
    } else {
      const step = this.moveIndex[this.currentStepIdx];
      this.stepText.setText(
        `[T${step.turn} ${step.side.toUpperCase()}]  Move ${this.currentStepIdx + 1} / ${total}${step.isTurnEnd ? '  · TURN END' : ''}`,
      );
    }

    this.updateLogPanel(state.log);
    this.updateNavButtons();

    // Only show the banner while actually viewing the final step — stepping
    // back (Prev Move/Turn) should clear it so it doesn't block the board.
    const atFinalStep = this.currentStepIdx === this.moveIndex.length - 1;
    if (state.phase === 'ended' && state.winner && atFinalStep) {
      this.showGameOverBanner(state.winner);
    } else {
      this.clearGameOverBanner();
    }

    this.prefetchNeighbors();
  }

  private renderUnits(state: GameState): void {
    const seen = new Set<string>();
    for (const unit of state.units) {
      seen.add(unit.id);
      let sprite = this.unitSprites.get(unit.id);
      if (!sprite) {
        sprite = new UnitSprite(this, unit, BOARD_X, BOARD_Y, false);
        this.unitSprites.set(unit.id, sprite);
      } else {
        sprite.update(unit, BOARD_X, BOARD_Y, false);
      }
    }
    for (const [id, sprite] of this.unitSprites) {
      if (!seen.has(id)) { sprite.destroy(); this.unitSprites.delete(id); }
    }
  }

  private updateLogPanel(log: LogEntry[]): void {
    if (log.length === 0) {
      this.logDiv.innerHTML = '<span style="color:#445544">No events yet.</span>';
      return;
    }
    const html = log.map(e => {
      const side  = e.side === 'allies' ? '<span style="color:#7799ff">A</span>' : '<span style="color:#ff7777">X</span>';
      const color = LOG_COLOR[e.type];
      const text  = e.text.replace(/&/g, '&amp;').replace(/</g, '&lt;');
      return `<div style="margin-bottom:2px">[T${e.turn} ${side}] <span style="color:${color}">${text}</span></div>`;
    }).join('');
    this.logDiv.innerHTML = html;
    this.logDiv.scrollTop = this.logDiv.scrollHeight;
  }

  private updateNavButtons(): void {
    const atStart = this.currentStepIdx === -1;
    const atEnd   = this.currentStepIdx === this.moveIndex.length - 1;
    const setEnabled = (btn: Phaser.GameObjects.Rectangle, enabled: boolean) => btn.setAlpha(enabled ? 1 : 0.4);

    setEnabled(this.prevMoveBtn, !atStart);
    setEnabled(this.prevTurnBtn, this.findPrevTurnIdx() !== this.currentStepIdx);
    setEnabled(this.nextMoveBtn, !atEnd);
    setEnabled(this.nextTurnBtn, this.findNextTurnIdx() !== this.currentStepIdx);
  }

  private showGameOverBanner(winner: Side): void {
    if (this.gameOverObjects.length > 0) return; // already showing
    this.gameOverObjects = [
      this.add.rectangle(W / 2, H / 2, 460, 180, 0x000000, 0.85).setDepth(20),
      this.add.text(W / 2, H / 2 - 35, 'GAME OVER', {
        fontSize: '32px', color: '#d4af37', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(21),
      this.add.text(W / 2, H / 2 + 15, `${winner.toUpperCase()} FORCES WIN!`, {
        fontSize: '22px', color: winner === 'allies' ? '#7799ff' : '#ff7777',
      }).setOrigin(0.5).setDepth(21),
    ];
  }

  private clearGameOverBanner(): void {
    for (const obj of this.gameOverObjects) obj.destroy();
    this.gameOverObjects = [];
  }
}
